import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IAccount, UserViewModel } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import { UserSessionViewModelKey } from '../../../../models/AppState';
import { AccountUsersViewModel } from '../../../../viewmodels/AdminViewModels';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { UserSelectModal } from '../../../../web/components/UserSelectModal';
import { styleSheet } from './styles';

interface IProps
	extends IEventLoggingComponentProps,
		IImpersonationContextComponentProps,
		IAdminUserSessionComponentProps {
	account?: IAccount;
	admins?: UserViewModel[];
	defaultOption?: ISelectOption<string | UserViewModel>;
	initialSelectedOption?: ISelectOption<string | UserViewModel>;
	adminsFilter?: (user: UserViewModel) => boolean;
	onOptionSelected?(option: ISelectOption<string | UserViewModel>, user?: UserViewModel): void;
	pinnedOptions?: ISelectOption<string | UserViewModel>[];
	onRenderPlaceholder?(): React.ReactNode;
	styles?: StyleDeclarationValue[];
}

interface IState {
	admins?: UserViewModel[];
	isLoading?: boolean;
	userSelectModalProps?: {
		isOpen: boolean;
		onRequestClose(user?: UserViewModel, canceled?: boolean): void;
	};
}

class _AccountUserSelectBox extends React.Component<IProps, IState> {
	private mSelectedUser: UserViewModel;
	private mSearchedUser: UserViewModel;
	private mSelectedConnectionType: string;
	private mDefaultAdminUser: UserViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_AccountUserSelectBox,
			| 'mSelectedUser'
			| 'mSearchedUser'
			| 'mSelectedConnectionType'
			| 'mDefaultAdminUser'
			| 'selectedValue'
			| 'selectedDisplayText'
			| 'onValueChange'
		>(this, {
			mSelectedUser: observable.ref,
			mSearchedUser: observable.ref,
			mSelectedConnectionType: observable.ref,
			mDefaultAdminUser: observable.ref,
			selectedValue: computed,
			selectedDisplayText: computed,
			onValueChange: action,
		});

		this.state = {
			isLoading: true,
			userSelectModalProps: {
				isOpen: false,
				onRequestClose: this.onUserSelectRequestClose,
			},
		};
		if (!!props.initialSelectedOption?.dataContext && typeof props.initialSelectedOption?.dataContext === 'object') {
			this.mSelectedUser = props.initialSelectedOption.dataContext;
			this.mSearchedUser = props.initialSelectedOption.dataContext;
		}
	}

	public async componentDidMount() {
		const {
			logApiError,
			logEvent,
			admins: propsAdmins,
			impersonationContext,
			userSession,
			account,
			adminsFilter,
		} = this.props;
		const nextState: IState = {
			isLoading: false,
		};

		if (!propsAdmins || propsAdmins?.length === 0) {
			logEvent('LoadAdmins');
			try {
				const users = new AccountUsersViewModel(
					userSession,

					account || impersonationContext?.account || userSession.account.toJs()
				);
				await users.loadAdmins();

				nextState.admins = adminsFilter ? users.admins.filter(adminsFilter) : users.admins;

				this.mDefaultAdminUser = users.admins.length > 0 ? users.admins[0] : null;
			} catch (error) {
				logApiError('LoadAdmins-Error', error);
			}
		} else {
			nextState.admins = propsAdmins;
			this.mDefaultAdminUser = propsAdmins?.[0];
		}
		this.setState(nextState);
	}

	public render() {
		const { styles, defaultOption, pinnedOptions, onRenderPlaceholder, impersonationContext } = this.props;
		const { userSelectModalProps, isLoading, admins } = this.state;
		const isDisabled = !this.mDefaultAdminUser || isLoading;

		return (
			<>
				<Select value={this.selectedValue} onValueChange={this.onValueChange}>
					<SelectTrigger disabled={isDisabled} styles={Array.isArray(styles) ? styles[0] : styles}>
						<span>{this.selectedDisplayText || (onRenderPlaceholder?.() ?? null)}</span>
					</SelectTrigger>
					<SelectContent>
						{defaultOption && <SelectItem value={defaultOption.id}>{defaultOption.text}</SelectItem>}
						{(pinnedOptions || []).map(option => (
							<SelectItem key={option.id} value={String(option.dataContext)}>
								{option.text}
							</SelectItem>
						))}
						{(admins || []).map(x => (
							<SelectItem key={`option-${x.id}`} value={x.id}>
								<div className={css(styleSheet.option)}>
									<div>{`${x.name} (Admin)`}</div>
									{!!x.email && <div className={css(styleSheet.selectOptionEmail)}>{x.email}</div>}
								</div>
							</SelectItem>
						))}
						{this.mSearchedUser && !admins?.find(x => x.id === this.mSearchedUser.id) && (
							<SelectItem value={this.mSearchedUser.id}>
								<div className={css(styleSheet.option)}>
									<div>{this.mSearchedUser.name}</div>
									{!!this.mSearchedUser.email && (
										<div className={css(styleSheet.selectOptionEmail)}>{this.mSearchedUser.email}</div>
									)}
								</div>
							</SelectItem>
						)}
						<SelectItem value='search'>Search Employee</SelectItem>
					</SelectContent>
				</Select>
				<UserSelectModal
					impersonationContext={impersonationContext}
					modalProps={userSelectModalProps}
					title='User Search'
				/>
			</>
		);
	}

	private get selectedValue(): string | null {
		if (this.mSelectedUser) {
			return this.mSelectedUser.id;
		}
		if (this.mSelectedConnectionType) {
			return this.mSelectedConnectionType;
		}
		const { defaultOption } = this.props;
		return defaultOption?.id ?? null;
	}

	private get selectedDisplayText(): string {
		if (this.mSelectedUser) {
			return this.mSelectedUser.name;
		}
		if (this.mSelectedConnectionType) {
			const match = (this.props.pinnedOptions || []).find(o => String(o.dataContext) === this.mSelectedConnectionType);
			return match?.text ?? '';
		}
		const { defaultOption } = this.props;
		return defaultOption?.text ?? '';
	}

	private onValueChange = (value: string | null) => {
		const { onOptionSelected, pinnedOptions, defaultOption } = this.props;

		if (value === null) {
			return;
		}

		if (value === 'search') {
			this.setState({
				userSelectModalProps: {
					...this.state.userSelectModalProps,
					isOpen: true,
				},
			});
			return;
		}

		if (value.startsWith('connection-type-')) {
			this.mSelectedConnectionType = value;
			this.mSelectedUser = null;
			const option = (pinnedOptions || []).find(o => String(o.dataContext) === value);
			onOptionSelected?.(option, this.mSelectedUser || this.mDefaultAdminUser);
			return;
		}

		if (defaultOption && value === defaultOption.id) {
			this.mSelectedUser = null;
			this.mSelectedConnectionType = null;
			onOptionSelected?.(defaultOption, this.mDefaultAdminUser);
			return;
		}

		const user =
			(this.state.admins || []).find(x => x.id === value) ??
			(this.mSearchedUser?.id === value ? this.mSearchedUser : undefined);
		if (user) {
			this.mSelectedUser = user;
			this.mSelectedConnectionType = null;
			const option: ISelectOption<UserViewModel> = {
				dataContext: user,
				id: `option-${user.id}`,
				text: user.name,
			};
			onOptionSelected?.(option, user);
		}
	};

	private onUserSelectRequestClose = async (user?: UserViewModel, cancel?: boolean) => {
		const { onOptionSelected } = this.props;
		const nextState: IState = {
			userSelectModalProps: {
				...this.state.userSelectModalProps,
				isOpen: false,
			},
		};

		if (!cancel) {
			this.mSelectedUser = user;
			this.mSearchedUser = user;
			onOptionSelected?.(
				{
					dataContext: this.mSelectedUser,

					id: this.mSelectedUser.id,
					text: this.mSelectedUser?.name,
				},
				this.mSelectedUser
			);
		}
		this.setState(nextState);
	};
}

const AccountUserSelectBoxAsObserver = observer(_AccountUserSelectBox);
const AccountUserSelectBoxWithContext = inject(
	UserSessionViewModelKey,
	ImpersonationContextKey
)(AccountUserSelectBoxAsObserver);
export const AccountUserSelectBox = withEventLogging(AccountUserSelectBoxWithContext, 'UserSelectBox');
